import React from "react";
import { observer } from "mobx-react";
import { Alert, Box, CircularProgress, Paper, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import { AlertsBatchAlert } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import {
  AlertsDashboardStore,
  AlertsEmailView,
} from "../../../../stores/dashboards/AlertsDashboardStore";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import { AlertsPill } from "./AlertsDataTable";
import {
  AlertsInfoGrid,
  AlertsInlineLink,
  AlertsSimpleColumn,
  AlertsSimpleTable,
} from "./AlertsDetailParts";
import { AlertsEmailLogTable } from "./AlertsAlertDetail";
import { AlertsEmptyBox } from "./AlertsListPanel";
import {
  formatAlertsCount,
  formatAlertsDateTime,
  formatAlertsText,
} from "./alertsDashboardFormat";

interface AlertsEmailDetailProps {
  store: AlertsDashboardStore;
  view: AlertsEmailView;
}

/** `POST /emails/:batchId` — the email, its alerts, and every send / SendGrid event. */
const AlertsEmailDetail: React.FC<AlertsEmailDetailProps> = ({
  store,
  view,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const detail = view.result;

  if (view.loading && !detail) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 220,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (!detail) {
    return view.error ? (
      <Alert
        severity="error"
        sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
      >
        {view.error}
      </Alert>
    ) : null;
  }

  const { batch } = detail;

  const alertColumns: AlertsSimpleColumn<AlertsBatchAlert>[] = [
    { label: "Alert ID", render: (row) => String(row.alert_id) },
    {
      label: "Created",
      render: (row) => formatAlertsDateTime(row.alert_created_at),
    },
    { label: "Type", render: (row) => row.type_code },
    { label: "Severity", render: (row) => <AlertsPill value={row.severity} /> },
    { label: "Title", render: (row) => row.title },
    { label: "Ref", render: (row) => formatAlertsText(row.ref_display) },
    {
      label: "Alert status",
      render: (row) => <AlertsPill value={row.alert_status} />,
    },
    {
      label: "Delivery",
      render: (row) => <AlertsPill value={row.delivery_status} />,
    },
    {
      label: "Delivery error",
      render: (row) => formatAlertsText(row.delivery_last_error),
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {view.error ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
        >
          {view.error}
        </Alert>
      ) : null}

      <Paper
        variant="outlined"
        sx={{ p: 2, borderRadius: 2, borderColor: customColors.softCardBorder }}
      >
        <Box sx={{ mb: 1 }}>
          <AlertsPill value={batch.status} />
        </Box>
        <AlertsInfoGrid
          items={[
            {
              label: "User",
              value: (
                <AlertsInlineLink
                  label={batch.user_name || `#${batch.user_id}`}
                  onClick={() =>
                    void store.openRecipient(
                      "CUSTOMER_USER",
                      batch.user_id,
                      batch.user_name,
                    )
                  }
                />
              ),
            },
            { label: "To", value: batch.to_email },
            {
              label: "Customer",
              value: `${batch.customer_name || "Customer"} (#${batch.customer_id})`,
            },
            {
              label: "Alerts in email",
              value: formatAlertsCount(batch.alerts),
            },
            { label: "Attempts", value: String(batch.attempts) },
            { label: "Created", value: formatAlertsDateTime(batch.created_at) },
            {
              label: "Enqueued",
              value: formatAlertsDateTime(batch.enqueued_at),
            },
            {
              label: "Next attempt",
              value: formatAlertsDateTime(batch.next_attempt_at),
            },
            { label: "Sent", value: formatAlertsDateTime(batch.sent_at) },
            {
              label: "Delivered",
              value: formatAlertsDateTime(batch.delivered_at),
            },
            { label: "Failed", value: formatAlertsDateTime(batch.failed_at) },
            { label: "Updated", value: formatAlertsDateTime(batch.updated_at) },
            { label: "Message ID", value: batch.provider_message_id },
            { label: "Last error", value: batch.last_error },
          ]}
        />
      </Paper>

      <CustomTypography variant="heading7" sx={{ color: colors.title }}>
        Alerts in this email ({detail.alerts.length})
      </CustomTypography>
      {detail.alerts.length > 0 ? (
        <AlertsSimpleTable
          columns={alertColumns}
          rows={detail.alerts}
          rowKey={(row) => String(row.delivery_id)}
          onRowClick={(row) => void store.openAlert(row.alert_id, row.title)}
        />
      ) : (
        <AlertsEmptyBox message="No alerts in this email." />
      )}

      <CustomTypography variant="heading7" sx={{ color: colors.title }}>
        Send attempts and SendGrid events ({detail.logs.length})
      </CustomTypography>
      <AlertsEmailLogTable logs={detail.logs} />
    </Box>
  );
};

export default observer(AlertsEmailDetail);
